/**
 * Handing a `mailto:` to the user's own mail client, without taking the SPA
 * down with it.
 *
 * Ported deliberately unchanged from `apps/web/lib/utils/mailto.ts`, because
 * the shape of it is the whole point. There are two kinds of mail handler and
 * they fail differently:
 *
 * - **OS app** (Apple Mail, Outlook): the browser hands the URL to the app and
 *   performs no navigation. `window.location.href = 'mailto:...'` is still a
 *   top-level navigation *attempt* though, so the document gets torn down and
 *   anything the caller awaits afterward — logging the email, marking the
 *   invoice sent — is canceled mid-flight.
 *
 * - **Web handler** (Gmail via `registerProtocolHandler`): the browser rewrites
 *   the `mailto:` into an `https://mail.google.com/...` URL and then does an
 *   ordinary navigation to it, landing in whichever browsing context the link
 *   targets. Without an explicit target that replaces Billable in the current
 *   tab. This is the case v1's bug was reported against.
 *
 * Clicking a detached anchor with `target="_blank"` covers both: the OS handler
 * still fires and leaves this document alone, and the Gmail compose window
 * opens in a new tab instead of on top of us.
 *
 * The click must happen synchronously inside the user's own click handler.
 * Awaiting a fetch first spends the transient activation and the `_blank` open
 * is then treated as an unsolicited popup.
 */
export interface MailtoDraft {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
}

/**
 * `mailto:` URL for a draft. Recipients go in the path, everything else in the
 * query — `encodeURIComponent` throughout, since a subject with an `&` in it
 * would otherwise silently truncate the body.
 */
export function buildMailtoUrl(draft: MailtoDraft): string {
  const params = new URLSearchParams();
  if (draft.cc?.length) params.set('cc', draft.cc.join(','));
  if (draft.bcc?.length) params.set('bcc', draft.bcc.join(','));
  params.set('subject', draft.subject);
  params.set('body', draft.body);

  // URLSearchParams encodes spaces as `+`, which mail clients render literally
  // in the body. Percent-encoding is what every mailto handler agrees on.
  const query = params.toString().replace(/\+/g, '%20');
  // Recipients stay raw: percent-encoding the comma separator makes some
  // clients read the whole list as one malformed address.
  return `mailto:${draft.to.join(',')}?${query}`;
}

/**
 * Where the user writes the email. `mailto:` alone proved unreliable: the OS
 * decides what handles it, and when that is another browser or a web app the
 * `_blank` tab opened for it is left on about:blank with no compose window at
 * all. Gmail and Outlook have compose URLs that take the same fields, so those
 * open directly in a new tab and never touch the protocol handler. Copy is the
 * way out for everything else (Yahoo, Proton, a locked-down work machine): the
 * user pastes the fields into whatever they use.
 */
export type ComposeApp = 'gmail' | 'outlook' | 'mail_app' | 'copy';

export const COMPOSE_APPS: { id: ComposeApp; label: string }[] = [
  { id: 'gmail', label: 'Gmail' },
  { id: 'outlook', label: 'Outlook' },
  { id: 'mail_app', label: 'Mail app' },
  { id: 'copy', label: 'Copy' }
];

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  return search.toString().replace(/\+/g, '%20');
}

export function buildComposeUrl(app: Exclude<ComposeApp, 'copy'>, draft: MailtoDraft): string {
  const to = draft.to.join(',');
  const cc = draft.cc?.length ? draft.cc.join(',') : undefined;
  const bcc = draft.bcc?.length ? draft.bcc.join(',') : undefined;

  if (app === 'gmail') {
    return `https://mail.google.com/mail/?${query({
      view: 'cm',
      fs: '1',
      to,
      cc,
      bcc,
      su: draft.subject,
      body: draft.body
    })}`;
  }
  if (app === 'outlook') {
    return `https://outlook.office.com/mail/deeplink/compose?${query({
      to,
      cc,
      bcc,
      subject: draft.subject,
      body: draft.body
    })}`;
  }
  return buildMailtoUrl(draft);
}

/**
 * Opens the compose window. Must run synchronously in the click handler, for
 * the same reason as described at the top of this file.
 *
 * The mail-app path deliberately has NO target. A `mailto:` is handed to the
 * OS without navigating, so this document survives, and there is no new tab to
 * be stranded on about:blank when the handler lives outside this browser.
 */
export function openCompose(app: ComposeApp, draft: MailtoDraft): void {
  if (typeof document === 'undefined' || app === 'copy') return;

  const link = document.createElement('a');
  link.href = buildComposeUrl(app, draft);
  if (app !== 'mail_app') {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const COMPOSE_APP_KEY = 'billable.composeApp';

/** The last choice in this browser. Gmail when there is none. */
export function readComposeApp(): ComposeApp {
  try {
    const saved = window.localStorage.getItem(COMPOSE_APP_KEY);
    if (saved === 'gmail' || saved === 'outlook' || saved === 'mail_app' || saved === 'copy') {
      return saved;
    }
  } catch {
    // Storage blocked: fall through to the default.
  }
  return 'gmail';
}

export function saveComposeApp(app: ComposeApp): void {
  try {
    window.localStorage.setItem(COMPOSE_APP_KEY, app);
  } catch {
    // Not remembered; harmless.
  }
}
